/**
 * Error Logging Types
 *
 * Comprehensive type definitions for the error logging system
 */

export type ErrorType =
  | 'global_error'
  | 'unhandled_promise_rejection'
  | 'react_error_boundary'
  | 'api_error'
  | 'validation_error'
  | 'authentication_error'
  | 'permission_error'
  | 'network_error'
  | 'component_error'
  | 'hook_error'
  | 'routing_error'
  | 'performance_error';

export type LogLevel = 'error' | 'warn' | 'info' | 'debug';

/**
 * Base error context that can be attached to any error
 */
export interface ErrorContext {
  type: ErrorType;
  userId?: string;
  userEmail?: string;
  userAgent?: string;
  url?: string;
  timestamp?: number;
  sessionId?: string;
  feature?: string;
  component?: string;
  action?: string;
  metadata?: Record<string, any>;
  breadcrumbs?: ErrorBreadcrumb[];
  performance?: PerformanceMetrics;
}

/**
 * API-specific error context
 */
export interface ApiErrorContext extends Partial<ErrorContext> {
  method?: string;
  endpoint?: string;
  status?: number;
  statusText?: string;
  requestHeaders?: Record<string, string>;
  responseHeaders?: Record<string, string>;
  requestBody?: any;
  responseBody?: any;
  duration?: number;
}

/**
 * React-specific error context
 */
export interface ReactErrorContext extends Partial<ErrorContext> {
  componentStack?: string;
  errorBoundary?: string;
  componentName?: string;
  props?: Record<string, any>;
  state?: Record<string, any>;
  hooks?: string[];
}

/**
 * Error breadcrumb for tracking user actions leading to error
 */
export interface ErrorBreadcrumb {
  timestamp: number;
  category: 'navigation' | 'user_action' | 'api_call' | 'error' | 'info';
  message: string;
  level: LogLevel;
  data?: Record<string, any>;
}

/**
 * Performance metrics
 */
export interface PerformanceMetrics {
  navigationTiming?: PerformanceNavigationTiming;
  memoryUsage?: {
    usedJSHeapSize?: number;
    totalJSHeapSize?: number;
    jsHeapSizeLimit?: number;
  };
  connectionType?: string;
  renderTime?: number;
  componentMountTime?: number;
}

/**
 * Enhanced Error class with context
 */
export class ContextualError extends Error {
  public readonly context: ErrorContext;
  public readonly originalError?: Error;
  public readonly timestamp: number;

  constructor(message: string, context: ErrorContext, originalError?: Error) {
    super(message);
    this.name = 'ContextualError';
    this.context = context;
    this.originalError = originalError;
    this.timestamp = Date.now();

    // Maintain proper stack trace for V8
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ContextualError);
    }
  }
}

/**
 * Error logger interface
 */
export interface IErrorLogger {
  logError(
    error: Error | ContextualError,
    context?: Partial<ErrorContext>
  ): void;
  logWarning(message: string, context?: Partial<ErrorContext>): void;
  logInfo(message: string, context?: Partial<ErrorContext>): void;
  logDebug(message: string, context?: Partial<ErrorContext>): void;
  logApiError(error: Error, apiContext: ApiErrorContext): void;
  logReactError(error: Error, reactContext: ReactErrorContext): void;
  addBreadcrumb(breadcrumb: ErrorBreadcrumb): void;
  setUserContext(userId: string, userEmail?: string): void;
  clearUserContext(): void;
}

/**
 * Error rate limiting interface
 */
export interface IErrorRateLimit {
  shouldLogError(error: Error): boolean;
  reset(): void;
}

/**
 * Data sanitization interface
 */
export interface IDataSanitizer {
  sanitizeObject(obj: any): any;
  sanitizeUrl(url: string): string;
  sanitizeHeaders(headers: Record<string, string>): Record<string, string>;
}
